/**
 * MyProgressPanel — Analytics personal del alumno.
 *
 * Muestra su propio rendimiento: XP, nivel, accuracy, ranking vs curso,
 * rendimiento por caso y actividad de los últimos 30 días. Disponible
 * para cualquier usuario autenticado (el teacher también puede verlo,
 * aunque tenga además el Dashboard).
 */

import { authService } from '../auth/AuthService';

interface CasePerformance {
  case_id: string;
  attempts: number;
  accuracy: number;
  best_score: number;
  avg_duration_seconds: number;
}
interface DailyPoint {
  date: string;
  attempts: number;
  correct: number;
}
interface MyStatsResponse {
  user_id: number;
  display_name: string;
  xp: number;
  level: number;
  cases_completed: number;
  cases_correct: number;
  accuracy: number;
  current_streak: number;
  longest_streak: number;
  last_activity_at: string | null;
  cohort_avg_xp: number;
  cohort_avg_accuracy: number;
  rank_by_xp: number;
  cohort_size: number;
  per_case: CasePerformance[];
  daily_activity: DailyPoint[];
}

const PANEL_ID = 'cgt-my-progress';
const STYLE_ID = 'cgt-my-progress-styles';

export class MyProgressPanel {
  private root: HTMLElement | null = null;

  async open(): Promise<void> {
    if (!authService.isAuthenticated) return;
    this.injectStyles();
    this.close();

    const root = document.createElement('div');
    root.id = PANEL_ID;
    root.innerHTML = `
      <div class="cgt-mp-backdrop" data-close></div>
      <div class="cgt-mp-card">
        <header>
          <div>
            <h2>Mi progreso</h2>
            <p>Tu rendimiento comparado con el curso.</p>
          </div>
          <button type="button" class="cgt-mp-close" data-close>×</button>
        </header>
        <div class="cgt-mp-body"><div class="cgt-mp-loading">Cargando…</div></div>
      </div>
    `;
    root.querySelectorAll<HTMLElement>('[data-close]').forEach(el =>
      el.addEventListener('click', () => this.close())
    );
    document.body.appendChild(root);
    requestAnimationFrame(() => root.classList.add('cgt-mp-visible'));
    this.root = root;

    let data: MyStatsResponse;
    try {
      data = await authService.request<MyStatsResponse>('/progress/stats');
    } catch (error) {
      const body = root.querySelector<HTMLElement>('.cgt-mp-body');
      if (body) body.innerHTML = `<div class="cgt-mp-error">No se pudo cargar: ${this.escape(String(error))}</div>`;
      return;
    }

    this.render(data);
  }

  close(): void {
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-mp-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
  }

  private render(d: MyStatsResponse): void {
    const body = this.root?.querySelector<HTMLElement>('.cgt-mp-body');
    if (!body) return;

    const accPct = Math.round(d.accuracy * 100);
    const cohortAccPct = Math.round(d.cohort_avg_accuracy * 100);
    const xpVsCohort = d.cohort_avg_xp > 0 ? Math.round(((d.xp - d.cohort_avg_xp) / d.cohort_avg_xp) * 100) : 0;
    const xpDelta = xpVsCohort >= 0 ? `+${xpVsCohort}%` : `${xpVsCohort}%`;

    const maxDaily = Math.max(1, ...d.daily_activity.map(p => p.attempts));

    body.innerHTML = `
      <div class="cgt-mp-hero">
        <div class="cgt-mp-hero-left">
          <div class="cgt-mp-greeting">Hola, <strong>${this.escape(d.display_name)}</strong></div>
          <div class="cgt-mp-level-row">
            <span class="cgt-mp-level">Nivel ${d.level}</span>
            <span class="cgt-mp-xp">${d.xp} XP</span>
            <span class="cgt-mp-rank">Ranking ${d.rank_by_xp} de ${d.cohort_size}</span>
          </div>
        </div>
        <div class="cgt-mp-hero-right">
          <div class="cgt-mp-streak">
            🔥 <strong>${d.current_streak}</strong>
            <span>racha actual</span>
            <small>mejor: ${d.longest_streak}</small>
          </div>
        </div>
      </div>

      <div class="cgt-mp-kpis">
        ${this.kpi('Casos completados', d.cases_completed)}
        ${this.kpi('Correctos', d.cases_correct)}
        ${this.kpi('Mi precisión', `${accPct}%`)}
        ${this.kpi('Promedio curso', `${cohortAccPct}%`)}
      </div>

      <div class="cgt-mp-compare">
        <h4>Tu XP vs. el promedio del curso</h4>
        <div class="cgt-mp-compare-row">
          <span class="cgt-mp-compare-label">Tu XP</span>
          <div class="cgt-mp-compare-bar"><div style="width:${Math.min(100, (d.xp / Math.max(1, Math.max(d.xp, d.cohort_avg_xp))) * 100)}%;background:#2997FF"></div></div>
          <span class="cgt-mp-compare-val">${d.xp}</span>
        </div>
        <div class="cgt-mp-compare-row">
          <span class="cgt-mp-compare-label">Promedio</span>
          <div class="cgt-mp-compare-bar"><div style="width:${Math.min(100, (d.cohort_avg_xp / Math.max(1, Math.max(d.xp, d.cohort_avg_xp))) * 100)}%;background:#6e6e73"></div></div>
          <span class="cgt-mp-compare-val">${Math.round(d.cohort_avg_xp)}</span>
        </div>
        <div class="cgt-mp-delta ${xpVsCohort >= 0 ? 'positive' : 'negative'}">${xpDelta} respecto al promedio</div>
      </div>

      <h4 class="cgt-mp-section">Rendimiento por caso</h4>
      ${d.per_case.length === 0 ? '<div class="cgt-mp-empty">Aún no has completado ningún caso.</div>' : `
        <table class="cgt-mp-table">
          <thead><tr><th>Caso</th><th>Intentos</th><th>Mejor score</th><th>Precisión</th><th>Duración prom.</th></tr></thead>
          <tbody>
            ${d.per_case.map(c => `
              <tr>
                <td>${this.escape(c.case_id)}</td>
                <td class="cgt-mp-mono">${c.attempts}</td>
                <td class="cgt-mp-mono">${c.best_score}</td>
                <td>${this.accuracyBar(c.accuracy)}</td>
                <td class="cgt-mp-mono">${Math.round(c.avg_duration_seconds)}s</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `}

      <h4 class="cgt-mp-section">Tu actividad (30 días)</h4>
      ${d.daily_activity.length === 0 ? '<div class="cgt-mp-empty">Sin actividad reciente.</div>' : `
        <div class="cgt-mp-daily">
          ${d.daily_activity.map(p => {
            const h = Math.max(4, Math.round((p.attempts / maxDaily) * 72));
            const ch = p.attempts ? Math.round((p.correct / p.attempts) * h) : 0;
            return `
              <div class="cgt-mp-bar-col" title="${p.date}: ${p.correct}/${p.attempts}">
                <div class="cgt-mp-bar-wrap" style="height:72px">
                  <div class="cgt-mp-bar" style="height:${h}px"></div>
                  <div class="cgt-mp-bar-c" style="height:${ch}px"></div>
                </div>
                <span>${p.date.slice(5)}</span>
              </div>
            `;
          }).join('')}
        </div>
      `}
    `;
  }

  private kpi(label: string, value: string | number): string {
    return `<div class="cgt-mp-kpi"><span>${this.escape(label)}</span><strong>${value}</strong></div>`;
  }

  private accuracyBar(acc: number): string {
    const pct = Math.round(acc * 100);
    const color = acc >= 0.8 ? '#30D158' : acc >= 0.5 ? '#FF9F0A' : '#FF453A';
    return `<div class="cgt-mp-acc"><div class="cgt-mp-acc-bar"><div style="width:${pct}%;background:${color}"></div></div><span class="cgt-mp-mono">${pct}%</span></div>`;
  }

  private escape(text: string): string {
    return text.replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c] ?? c));
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${PANEL_ID} {
        position: fixed; inset: 0; z-index: 14500;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        color: #f5f5f7;
        opacity: 0; transition: opacity 0.2s;
      }
      #${PANEL_ID}.cgt-mp-visible { opacity: 1; }
      #${PANEL_ID} .cgt-mp-backdrop {
        position: absolute; inset: 0; background: rgba(0,0,0,0.55);
        backdrop-filter: blur(4px);
      }
      #${PANEL_ID} .cgt-mp-card {
        position: relative; max-width: 760px; width: 94vw; max-height: 86vh;
        margin: 7vh auto; background: #0a0a0a;
        border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 18px; display: flex; flex-direction: column;
        overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,0.5);
      }
      #${PANEL_ID} header {
        display: flex; align-items: start; justify-content: space-between;
        padding: 20px 24px; border-bottom: 0.5px solid rgba(255,255,255,0.06);
      }
      #${PANEL_ID} header h2 { font-size: 20px; font-weight: 700; letter-spacing: -0.3px; }
      #${PANEL_ID} header p { font-size: 12px; color: #a1a1a6; margin-top: 4px; }
      #${PANEL_ID} .cgt-mp-close {
        width: 28px; height: 28px; border-radius: 50%;
        background: rgba(255,255,255,0.06); border: none; cursor: pointer;
        color: #a1a1a6; font-size: 18px;
      }
      #${PANEL_ID} .cgt-mp-body { padding: 18px 24px 24px; overflow-y: auto; flex: 1; }
      #${PANEL_ID} .cgt-mp-loading, #${PANEL_ID} .cgt-mp-empty, #${PANEL_ID} .cgt-mp-error {
        padding: 30px; text-align: center; color: #a1a1a6;
      }
      #${PANEL_ID} .cgt-mp-error { color: #FF6961; }

      #${PANEL_ID} .cgt-mp-hero {
        display: flex; justify-content: space-between; align-items: center;
        padding: 18px; margin-bottom: 14px;
        background: linear-gradient(135deg, rgba(41,151,255,0.12), rgba(191,90,242,0.12));
        border: 0.5px solid rgba(41,151,255,0.25);
        border-radius: 14px;
      }
      #${PANEL_ID} .cgt-mp-greeting { font-size: 14px; color: #a1a1a6; }
      #${PANEL_ID} .cgt-mp-greeting strong { color: #f5f5f7; font-weight: 700; }
      #${PANEL_ID} .cgt-mp-level-row {
        display: flex; gap: 10px; margin-top: 8px; align-items: center;
        flex-wrap: wrap;
      }
      #${PANEL_ID} .cgt-mp-level {
        padding: 4px 10px; border-radius: 6px;
        background: rgba(191,90,242,0.2); color: #C084FC;
        font-size: 11px; font-weight: 700;
        font-family: 'JetBrains Mono', monospace;
      }
      #${PANEL_ID} .cgt-mp-xp {
        font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600;
        color: #2997FF;
      }
      #${PANEL_ID} .cgt-mp-rank { font-size: 11px; color: #a1a1a6; }
      #${PANEL_ID} .cgt-mp-streak {
        text-align: right;
      }
      #${PANEL_ID} .cgt-mp-streak strong {
        font-family: 'JetBrains Mono', monospace; font-size: 22px;
        color: #FF9F0A; margin-right: 4px;
      }
      #${PANEL_ID} .cgt-mp-streak span {
        display: block; font-size: 10px; color: #a1a1a6;
        text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px;
      }
      #${PANEL_ID} .cgt-mp-streak small {
        display: block; font-size: 10px; color: #6e6e73; margin-top: 2px;
      }

      #${PANEL_ID} .cgt-mp-kpis {
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
        margin-bottom: 14px;
      }
      #${PANEL_ID} .cgt-mp-kpi {
        background: rgba(255,255,255,0.03); border-radius: 10px; padding: 12px;
      }
      #${PANEL_ID} .cgt-mp-kpi span {
        display: block; font-size: 10px; color: #6e6e73;
        text-transform: uppercase; letter-spacing: 0.5px;
      }
      #${PANEL_ID} .cgt-mp-kpi strong {
        display: block; font-family: 'JetBrains Mono', monospace;
        font-size: 18px; font-weight: 700; color: #fff; margin-top: 3px;
      }

      #${PANEL_ID} .cgt-mp-compare {
        background: rgba(255,255,255,0.02); padding: 14px;
        border-radius: 12px; margin-bottom: 14px;
      }
      #${PANEL_ID} .cgt-mp-compare h4 { font-size: 12px; color: #a1a1a6; margin-bottom: 10px; }
      #${PANEL_ID} .cgt-mp-compare-row {
        display: grid; grid-template-columns: 70px 1fr 50px;
        align-items: center; gap: 10px; margin-bottom: 6px;
      }
      #${PANEL_ID} .cgt-mp-compare-label { font-size: 11px; color: #a1a1a6; }
      #${PANEL_ID} .cgt-mp-compare-bar {
        height: 8px; background: rgba(255,255,255,0.05);
        border-radius: 4px; overflow: hidden;
      }
      #${PANEL_ID} .cgt-mp-compare-bar div { height: 100%; transition: width 0.3s; }
      #${PANEL_ID} .cgt-mp-compare-val {
        font-family: 'JetBrains Mono', monospace; font-size: 11px;
        color: #f5f5f7; text-align: right;
      }
      #${PANEL_ID} .cgt-mp-delta { font-size: 11px; margin-top: 8px; text-align: right; }
      #${PANEL_ID} .cgt-mp-delta.positive { color: #30D158; }
      #${PANEL_ID} .cgt-mp-delta.negative { color: #FF6961; }

      #${PANEL_ID} .cgt-mp-section {
        margin-top: 18px; margin-bottom: 10px; font-size: 13px; font-weight: 600;
      }
      #${PANEL_ID} .cgt-mp-table { width: 100%; border-collapse: collapse; font-size: 12px; }
      #${PANEL_ID} .cgt-mp-table th {
        text-align: left; padding: 8px 10px; color: #6e6e73;
        font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;
        border-bottom: 0.5px solid rgba(255,255,255,0.08);
      }
      #${PANEL_ID} .cgt-mp-table td {
        padding: 10px; border-bottom: 0.5px solid rgba(255,255,255,0.04);
      }
      #${PANEL_ID} .cgt-mp-mono { font-family: 'JetBrains Mono', monospace; font-size: 11px; }
      #${PANEL_ID} .cgt-mp-acc { display: flex; align-items: center; gap: 6px; }
      #${PANEL_ID} .cgt-mp-acc-bar {
        flex: 1; height: 4px; background: rgba(255,255,255,0.06);
        border-radius: 2px; overflow: hidden; min-width: 40px;
      }
      #${PANEL_ID} .cgt-mp-acc-bar div { height: 100%; transition: width 0.3s; }

      #${PANEL_ID} .cgt-mp-daily {
        display: flex; align-items: end; gap: 2px; padding: 8px 0;
        overflow-x: auto;
      }
      #${PANEL_ID} .cgt-mp-bar-col {
        display: flex; flex-direction: column; align-items: center;
        flex: 1 0 14px; min-width: 14px;
      }
      #${PANEL_ID} .cgt-mp-bar-wrap {
        width: 100%; position: relative;
        display: flex; align-items: end; justify-content: center;
      }
      #${PANEL_ID} .cgt-mp-bar {
        width: 70%; background: rgba(41,151,255,0.3); border-radius: 2px 2px 0 0;
        position: absolute; bottom: 0;
      }
      #${PANEL_ID} .cgt-mp-bar-c {
        width: 70%; background: #30D158; border-radius: 2px 2px 0 0;
        position: absolute; bottom: 0;
      }
      #${PANEL_ID} .cgt-mp-bar-col span {
        font-size: 8px; color: #6e6e73; margin-top: 4px;
        font-family: 'JetBrains Mono', monospace;
        transform: rotate(-45deg); white-space: nowrap;
        transform-origin: left top;
      }
    `;
    document.head.appendChild(style);
  }
}

export const myProgressPanel = new MyProgressPanel();
